<script setup>
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { listPromotions } from '../api/promotions.js'
import { local_date_start as isoStart, local_date_end as isoEnd } from '../lib/local-date-range.js'
import {
  getConversionFunnel,
  getConversionFunnelUsageActivity,
  getConversionFunnelPaypointActivity,
  getConversionFunnelPageFunnel,
  getConversionFunnelAuthActivity,
  getConversionFunnelDailyClaimActivity,
  getConversionFunnelTrafficVideos,
  getConversionFunnelVisitorEvents,
  getConversionFunnelVisitors,
} from '../api/analytics.js'

const pageHost = ref(null)
const reports = new Map()
const { t } = useI18n()
const device_types = ref([])
const device_filter_host = ref(null)
const device_options = ['desktop', 'mobile', 'tablet']

function deviceQuery() {
  return device_types.value.length ? { device_types: [...device_types.value].sort().join(',') } : {}
}

watch(device_types, () => {
  pageHost.value?.shadowRoot?.dispatchEvent(new Event('device-filter-change'))
}, { deep: true })

function reportFor(since, until, trafficScope) {
  const key = `${since}:${until}:${trafficScope}:${deviceQuery().device_types || 'all'}`
  if (!reports.has(key)) {
    reports.set(key, getConversionFunnel({
      since: isoStart(since),
      until: isoEnd(until),
      traffic_scope: trafficScope,
      ...deviceQuery(),
    }))
  }
  return reports.get(key)
}

function responsePayload(pathname, report) {
  if (pathname === '/api/traffic') {
    const socialVisits = report.traffic.social_visits
    const compatibleSocialVisits = Array.isArray(socialVisits)
      ? socialVisits
      : Object.assign([...(socialVisits?.comments || [])], socialVisits)
    return {
      platforms: report.traffic.platforms.map(platform => ({ ...platform, totalViews: platform.views })),
      socialVisits: compatibleSocialVisits,
      emailCampaigns: report.traffic.email_campaigns,
      acquisition: report.traffic.acquisition,
    }
  }
  if (pathname === '/api/website-visits') {
    return {
      pageViews: report.website.views,
      uniqueVisitors: report.website.users,
      pages: report.website.pages,
      locations: report.website.locations,
      devices: report.website.devices,
      sources: report.website.sources,
    }
  }
  if (pathname === '/api/registrations') {
    return {
      registration: {
        total: report.registrations.views,
        users: report.registrations.users,
        methods: report.registrations.methods,
      },
      login: {
        total: report.registrations.login.views,
        users: report.registrations.login.users,
        methods: report.registrations.login.methods,
        new_users: report.registrations.login.new_users,
        existing_users: report.registrations.login.existing_users,
      },
    }
  }
  if (pathname === '/api/website-usage') {
    return {
      textInputs: report.usage.text_input.views,
      textInputUsers: report.usage.text_input.users,
      voiceInputs: report.usage.voice_input.views,
      voiceInputUsers: report.usage.voice_input.users,
      sliceVideos: report.usage.slice_video.views,
      sliceVideoUsers: report.usage.slice_video.users,
      globalVideos: report.usage.global_video.views,
      globalVideoUsers: report.usage.global_video.users,
      freeGames: report.usage.free_game.views,
      freeGameUsers: report.usage.free_game.users,
    }
  }
  if (pathname === '/api/point-selection') {
    return {
      dailyClaims: report.points.daily_claim.views,
      dailyClaimUsers: report.points.daily_claim.users,
      purchasePageViews: report.points.purchase_page.views,
      purchasePageUsers: report.points.purchase_page.users,
    }
  }
  if (pathname === '/api/paypoints') {
    return {
      rewriteViews: report.paypoints.rewrite.views,
      rewriteUsers: report.paypoints.rewrite.users,
      videoViews: report.paypoints.video_generation.views,
      videoUsers: report.paypoints.video_generation.users,
    }
  }
  if (pathname === '/api/remaining-metrics') {
    return {
      orders: report.orders.views,
      orderUsers: report.orders.users,
      checkoutStarts: report.checkout_started.views,
      checkoutStartUsers: report.checkout_started.users,
      paymentFailures: report.payment_failure.views,
      paymentFailureUsers: report.payment_failure.users,
      paymentSuccesses: report.payment_success.views,
      paymentSuccessUsers: report.payment_success.users,
      paymentCanceled: report.payment_canceled.views,
      paymentCanceledUsers: report.payment_canceled.users,
      paymentPending: report.payment_pending.views,
      paymentPendingUsers: report.payment_pending.users,
      paymentRefunded: report.payment_refunded.views,
      paymentRefundedUsers: report.payment_refunded.users,
      paymentDisputed: report.payment_disputed.views,
      paymentDisputedUsers: report.payment_disputed.users,
      shareClicks: report.sharing.total.views,
      shareUsers: report.sharing.total.users,
    }
  }
  throw new Error(`Unsupported conversion funnel request: ${pathname}`)
}

async function funnelFetch(url) {
  const target = new URL(url, window.location.origin)
  if (target.pathname === '/api/page-funnel') {
    const report = await getConversionFunnelPageFunnel({ ...deviceQuery(), since: isoStart(target.searchParams.get('since')), until: isoEnd(target.searchParams.get('until')) })
    return { ok: true, json: async () => report }
  }
  if (target.pathname === '/api/behavior-visitors') {
    const visitors = await getConversionFunnelVisitors({
      ...deviceQuery(),
      since: isoStart(target.searchParams.get('since')),
      until: isoEnd(target.searchParams.get('until')),
      ...(target.searchParams.get('source') ? { source: target.searchParams.get('source') } : {}),
      ...(target.searchParams.get('page') ? { page: target.searchParams.get('page') } : {}),
    })
    return { ok: true, json: async () => visitors }
  }
  if (target.pathname === '/api/behavior-visitor-events') {
    const events = await getConversionFunnelVisitorEvents({
      ...deviceQuery(),
      since: isoStart(target.searchParams.get('since')),
      until: isoEnd(target.searchParams.get('until')),
      ...(target.searchParams.get('source') ? { source: target.searchParams.get('source') } : {}),
      ...(target.searchParams.get('page') ? { page: target.searchParams.get('page') } : {}),
    })
    return { ok: true, json: async () => events }
  }
  if (target.pathname === '/api/traffic-videos') {
    const videos = await getConversionFunnelTrafficVideos({
      ...deviceQuery(),
      since: isoStart(target.searchParams.get('since')),
      until: isoEnd(target.searchParams.get('until')),
      traffic_scope: target.searchParams.get('traffic_scope') || 'range',
      platform: target.searchParams.get('platform'),
    })
    return { ok: true, json: async () => videos }
  }
  if (target.pathname === '/api/auth-activity') {
    const activity = await getConversionFunnelAuthActivity({
      ...deviceQuery(),
      since: isoStart(target.searchParams.get('since')),
      until: isoEnd(target.searchParams.get('until')),
    })
    return { ok: true, json: async () => activity }
  }
  if (target.pathname === '/api/daily-claim-activity') {
    const activity = await getConversionFunnelDailyClaimActivity({
      ...deviceQuery(),
      since: isoStart(target.searchParams.get('since')),
      until: isoEnd(target.searchParams.get('until')),
    })
    return { ok: true, json: async () => activity }
  }
  if (['/api/usage-activity', '/api/paypoint-activity'].includes(target.pathname)) {
    const loadActivity = target.pathname === '/api/paypoint-activity' ? getConversionFunnelPaypointActivity : getConversionFunnelUsageActivity
    const activity = await loadActivity({
      ...deviceQuery(),
      since: isoStart(target.searchParams.get('since')),
      until: isoEnd(target.searchParams.get('until')),
      ...(target.searchParams.get('kind') ? { kind: target.searchParams.get('kind') } : {}),
    })
    return { ok: true, json: async () => activity }
  }
  if (target.pathname === '/api/website-visits') {
    const [report, promotion_report] = await Promise.all([
      reportFor(target.searchParams.get('since'), target.searchParams.get('until'), target.searchParams.get('traffic_scope') || 'range'),
      listPromotions({ since: isoStart(target.searchParams.get('since')), until: isoEnd(target.searchParams.get('until')) }),
    ])
    return { ok: true, json: async () => ({ ...responsePayload(target.pathname, report), promotions: promotion_report.links }) }
  }
  const report = await reportFor(
    target.searchParams.get('since'),
    target.searchParams.get('until'),
    target.searchParams.get('traffic_scope') || 'range',
  )
  return { ok: true, json: async () => responsePayload(target.pathname, report) }
}

function loadScript(src) {
  const existing = document.querySelector(`script[src="${src}"]`)
  if (existing) {
    if (window.THREE) return Promise.resolve()
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', resolve, { once: true })
      existing.addEventListener('error', reject, { once: true })
    })
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.addEventListener('load', resolve, { once: true })
    script.addEventListener('error', reject, { once: true })
    document.head.appendChild(script)
  })
}

function scopedDocument(root, host) {
  return {
    documentElement: host,
    getElementById: id => root.getElementById(id),
    querySelector: selector => root.querySelector(selector),
    createElement: tag => document.createElement(tag),
    videoExportLabels: Object.fromEntries(['button', 'published_sort', 'published_label', 'unknown'].map(key => [key, t(`visualGameAdmin.videoExport.${key}`)])),
    periodLabels: Object.fromEntries(['yesterday', 'last_three_days'].map(key => [key, t(`visualGameAdmin.funnelPeriods.${key}`)])),
    authLabels: Object.fromEntries(['registration', 'login', 'new_users', 'existing_users', 'note', 'user_type', 'registered_at', 'new_type', 'existing_type', 'auto_login'].map(key => [key, t(`visualGameAdmin.funnelAuth.${key}`)])),
    paypointLabels: {
      ...Object.fromEntries(['records', 'button', 'search', 'user', 'time', 'detail', 'empty', 'loading', 'error', 'limit'].map(key => [key, t(`visualGameAdmin.usageActivity.${key}`)])),
      ...Object.fromEntries(['search', 'title', 'all', 'game', 'kind', 'status', 'note', 'billing_paypoint_gameplay', 'billing_paypoint_ending_video', 'billing_paypoint_story_video', 'status_insufficient_balance', 'status_dialog_open'].map(key => [key, t(`visualGameAdmin.paypointActivity.${key}`)])),
    },
    usageLabels: Object.fromEntries(['records', 'title', 'button', 'all', 'search', 'user', 'game', 'kind', 'status', 'time', 'detail', 'empty', 'loading', 'error', 'note', 'limit', 'text_input', 'voice_input', 'slice_video', 'global_video', 'free_game', 'status_queued', 'status_running', 'status_ready', 'status_failed', 'status_submitting', 'status_submitted', 'status_ok', 'status_error', 'status_generating', 'status_generation_failed', 'status_pending_review', 'status_review_rejected', 'status_published', 'status_archived'].map(key => [key, t(`visualGameAdmin.usageActivity.${key}`)])),
    trafficLabels: Object.fromEntries(['confirmed', 'first', 'missing', 'note', 'observed', 'filter', 'unchanged', 'later'].map(key => [key, t(`visualGameAdmin.trafficBaseline.${key}`)])),
    promotionLabels: {
      title: t('visualGame.promotion.admin.funnelTitle'),
      note: t('visualGame.promotion.admin.funnelNote'),
      stages: Object.fromEntries(['open', 'loaded', 'interaction', 'limit_reached', 'registered', 'existing_login', 'continued'].map(key => [key, t(`visualGame.promotion.admin.stages.${key}`)])),
    },
    ordersLabel: t('visualGameAdmin.orderDrilldown.button'),
    funnelOrdersHref: (since, until, filters) => '/billing/orders?' + new URLSearchParams({ ...filters, since: isoStart(since), until: isoEnd(until), ...deviceQuery() }).toString(),
    funnelUsersHref: (operation, since, until) => '/users?' + new URLSearchParams({ funnel_operation: operation, since, until, ...deviceQuery() }).toString(),
    addEventListener: (...args) => root.addEventListener(...args),
  }
}

function themeBridge() {
  return `
    .usage-activity-tools { grid-template-columns: minmax(160px, 1fr) minmax(240px, 2fr); }
    @media (max-width: 640px) { .usage-activity-tools { grid-template-columns: 1fr; } }
    .page-funnel-chart { display: grid; gap: var(--drama-space-xs); padding: var(--drama-space-xl) 0; }
    .page-funnel-stage { position: relative; display: grid; place-items: center; min-height: calc(var(--drama-control-height-md) * 3); text-align: center; }
    .page-funnel-stage::before {
      content: '';
      position: absolute;
      inset: 0;
      background: var(--drama-color-primary);
      clip-path: polygon(var(--funnel-top) 0, calc(100% - var(--funnel-top)) 0, calc(100% - var(--funnel-bottom)) 100%, var(--funnel-bottom) 100%);
    }
    .page-funnel-stage:nth-child(2)::before { background: color-mix(in srgb, var(--drama-color-primary) 85%, var(--drama-color-text)); }
    .page-funnel-stage:nth-child(3)::before { background: color-mix(in srgb, var(--drama-color-primary) 70%, var(--drama-color-text)); }
    .page-funnel-label { position: relative; color: var(--drama-color-on-primary); }
    .page-funnel-stage strong { display: block; font-size: var(--drama-font-size-xl); margin: var(--drama-space-xs) 0; }
    .page-funnel-rate { position: absolute; right: var(--drama-space-sm); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
    .header-actions { gap: var(--drama-space-md); flex-wrap: wrap; }
    .filter-field { gap: var(--drama-space-xs); }
    .filter-field > span, .device-filter legend {
      padding: 0;
      color: var(--drama-color-text-muted);
      font-size: var(--drama-font-size-xs);
      font-weight: var(--drama-font-weight-semibold);
      line-height: 1.3;
      letter-spacing: normal;
    }
    .metric-select { box-sizing: border-box; height: var(--drama-control-height-md); }
    .device-filter { margin: 0; padding: 0; border: 0; min-width: 0; }
    .device-options {
      display: flex;
      align-items: center;
      gap: var(--drama-space-md);
      box-sizing: border-box;
      height: var(--drama-control-height-md);
      margin-top: var(--drama-space-xs);
      padding: 0 var(--drama-space-md);
      border: 1px solid var(--drama-color-border);
      border-radius: var(--drama-radius-sm);
      background: var(--drama-color-surface);
      color: var(--drama-color-text-muted);
      font-size: var(--drama-font-size-xs);
    }
    .device-option { display: inline-flex; align-items: center; gap: var(--drama-space-xs); white-space: nowrap; cursor: pointer; }
    .device-option input { margin: 0; accent-color: var(--drama-color-primary); }
    .device-option input:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 2px; }
    :host {
      display: block;
      color-scheme: inherit;
      --page: var(--drama-color-bg);
      --surface: var(--drama-color-surface);
      --ink: var(--drama-color-text);
      --ink-2: var(--drama-color-text-muted);
      --muted: var(--drama-color-text-muted);
      --grid: var(--drama-color-surface-alt);
      --border: var(--drama-color-border);
      --good: var(--drama-color-success);
      --critical: var(--drama-color-danger);
      --accent: var(--drama-color-primary);
      --spout: var(--drama-color-primary);
      --alert-red: var(--drama-color-danger);
      --alert-yellow: var(--drama-color-warning);
      --alert-blue: var(--drama-color-info);
      --alert-green: var(--drama-color-success);
      --shadow: var(--drama-shadow-sm);
      background: var(--drama-color-bg);
    }
  `
}

onMounted(async () => {
  const response = await window.fetch('/conversion-funnel.html', { cache: 'no-store' })
  if (!response.ok) throw new Error(`Failed to load conversion funnel: ${response.status}`)

  const source = await response.text()
  const parsed = new DOMParser().parseFromString(source, 'text/html')
  const mainScript = [...parsed.scripts].at(-1)?.textContent
  const content = parsed.querySelector('.wrap')
  const css = parsed.querySelector('style')?.textContent
  if (!pageHost.value || !mainScript || !content || !css) throw new Error('Invalid conversion funnel document')

  const root = pageHost.value.attachShadow({ mode: 'open' })
  const style = document.createElement('style')
  style.textContent = `${css}\n${themeBridge()}`
  root.append(style, content.cloneNode(true))
  const filter_container = document.createElement('div')
  root.querySelector('.header-actions').prepend(filter_container)
  device_filter_host.value = filter_container

  await loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.min.js')
  await loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js')

  const run = new Function('document', 'window', 'fetch', 'THREE', 'ResizeObserver', 'MutationObserver', 'requestAnimationFrame', 'pageFunnelLabels', mainScript)
  run(
    scopedDocument(root, pageHost.value),
    window,
    funnelFetch,
    window.THREE,
    ResizeObserver,
    MutationObserver,
    requestAnimationFrame,
    Object.fromEntries(['button', 'title', 'home', 'auth', 'game', 'note', 'loading', 'error', 'sessions', 'viewUsers', 'peopleUnit', 'timesUnit'].map(key => [key, t(`visualGameAdmin.pageFunnel.${key}`)])),
  )
})
</script>

<template>
  <div ref="pageHost" class="conversion-funnel-page" />
  <Teleport v-if="device_filter_host" :to="device_filter_host">
    <fieldset class="device-filter" data-testid="funnel-device-filter">
      <legend>{{ t('visualGameAdmin.conversionFunnelDevices.label') }}</legend>
      <div class="device-options">
        <label class="device-option">
          <input type="checkbox" :checked="device_types.length === 0" @change="device_types = []">
          <span>{{ t('visualGameAdmin.conversionFunnelDevices.all') }}</span>
        </label>
        <label v-for="device in device_options" :key="device" class="device-option">
          <input v-model="device_types" type="checkbox" :value="device" :data-testid="`funnel-device-${device}`">
          <span>{{ t(`visualGameAdmin.conversionFunnelDevices.${device}`) }}</span>
        </label>
      </div>
    </fieldset>
  </Teleport>
</template>

<style scoped>
.conversion-funnel-page {
  min-width: 0;
  background: var(--drama-color-bg);
}
</style>
